import { ChangeDetectorRef, Directive, OnInit, ViewChild, inject } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { MatSelect } from "@angular/material/select";
import { finalize, timeout } from "rxjs";

import { Empleado } from "../model/empleado/empleado.model";
import {
  IncidenciaIsoComun,
  RespuestaIso,
  ServicioIncidenciasIso,
  idUsuarioEmpleadoIso,
  nombreEmpleadoIso,
  perfilEmpleadoIso,
} from "./incidencias-iso.comun";

/** Datos con los que se abre la asignación de responsable. */
export interface DatosAsignacionIso<TIncidencia> {
  incidencia: TIncidencia;
  idUsuarioSesion?: number;
}

/**
 * Textos y clases que cambian entre la asignación de incidencias IA y la de seguridad; las dos pintan
 * la plantilla de incidencias-ia/asignar-responsable-ia.
 */
export interface VistaAsignacionIso {
  /** Clase del card (la que usa la hoja de estilos de cada pantalla). */
  claseModal: string;
  titulo: string;
  etiquetaResponsable: string;
  /** Clase del panel de opciones del select de responsables. */
  clasePanel: string;
  etiquetaMotivo: string;
  placeholderMotivo: string;
  sinResponsables: string;
}

/**
 * Asignación del responsable de una incidencia IA o de seguridad: carga los responsables (propone al
 * usuario de la sesión), pide el motivo y pasa la incidencia a proceso. Cada pantalla dice qué campos
 * del responsable llena y de dónde toma la lista.
 */
@Directive()
export abstract class AsignarResponsableIsoBase<TIncidencia extends IncidenciaIsoComun> implements OnInit {
  @ViewChild("responsableSelect") responsableSelect?: MatSelect;

  abstract readonly vista: VistaAsignacionIso;
  responsables: Empleado[] = [];
  idEmpleadoSeleccionado: number | null = null;
  comentarioReasignacion = "";
  cargando = false;
  guardando = false;
  error = "";
  readonly data = inject<DatosAsignacionIso<TIncidencia>>(MAT_DIALOG_DATA);
  protected abstract readonly incidenciasService: ServicioIncidenciasIso<TIncidencia>;
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly dialogRef = inject(MatDialogRef);

  /** Resumen de la incidencia que encabeza el diálogo. */
  abstract get resumenIncidencia(): string;

  ngOnInit(): void {
    this.cargarResponsables();
  }

  cargarResponsables(): void {
    this.cargando = true;
    this.error = "";
    this.incidenciasService
      .consultarResponsables()
      .pipe(
        timeout(15000),
        finalize(() => {
          this.cargando = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: (res) => this.recibirResponsables(res),
        error: (e) => {
          this.error = e?.name === "TimeoutError"
            ? "La carga de responsables tardo demasiado. Intenta de nuevo."
            : e?.error?.mensajeError || e?.error?.message || "No fue posible cargar los responsables.";
        },
      });
  }

  asignarme(): void {
    const propio = this.responsables.find((item) => idUsuarioEmpleadoIso(item) === this.data?.idUsuarioSesion);
    if (propio) {
      this.idEmpleadoSeleccionado = propio.idEmpleado;
    }
  }

  puedeAsignarme(): boolean {
    return this.responsables.some((item) => idUsuarioEmpleadoIso(item) === this.data?.idUsuarioSesion);
  }

  asignar(): void {
    if (!this.data?.incidencia?.idIncidenciaIa || !this.idEmpleadoSeleccionado) {
      this.error = "Selecciona un responsable.";
      return;
    }
    if (!this.comentarioReasignacion.trim()) {
      this.error = "Captura el motivo de la asignacion o reasignacion.";
      return;
    }
    const responsable = this.responsables.find((item) => item.idEmpleado === this.idEmpleadoSeleccionado);
    if (!responsable) {
      this.error = "El responsable seleccionado ya no esta disponible.";
      return;
    }
    const asignacion = this.crearAsignacion(responsable);
    this.guardando = true;
    this.error = "";
    this.incidenciasService
      .cambiarEstado(
        this.data.incidencia.idIncidenciaIa,
        "PROCESO",
        asignacion,
        {
          idIncidenciaIa: this.data.incidencia.idIncidenciaIa,
          comentario: this.comentarioReasignacion.trim(),
        },
      )
      .pipe(
        timeout(15000),
        finalize(() => {
          this.guardando = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: (res) => {
          if (!res || res.codigoError) {
            this.error = res?.mensajeError || "No fue posible asignar la incidencia.";
            return;
          }
          this.dialogRef.close({ saved: true, refresh: true, advertencia: res.advertencia || "" });
        },
        error: (e) => {
          this.error = e?.name === "TimeoutError"
            ? "La asignacion tardo demasiado. Revisa el tablero o intenta de nuevo."
            : e?.error?.mensajeError || e?.error?.message || "No fue posible asignar la incidencia.";
        },
      });
  }

  cerrar(): void {
    this.dialogRef.close({ cancelled: true });
  }

  onResponsableSeleccionado(): void {
    setTimeout(() => {
      this.responsableSelect?.close();
      this.cdr.detectChanges();
    });
  }

  nombreResponsable(empleado: Empleado): string {
    return nombreEmpleadoIso(empleado);
  }

  perfilResponsable(empleado: Empleado): string {
    return perfilEmpleadoIso(empleado);
  }

  /** Toma los responsables de la respuesta y propone al usuario de la sesión si está en la lista. */
  protected recibirResponsables(res: RespuestaIso<TIncidencia>): void {
    if (!res || res.codigoError) {
      this.error = res?.mensajeError || "No fue posible cargar los responsables.";
      return;
    }
    this.responsables = this.deduplicarResponsables(this.listaResponsables(res));
    const propio = this.responsables.find((item) => idUsuarioEmpleadoIso(item) === this.data?.idUsuarioSesion);
    if (propio) {
      this.idEmpleadoSeleccionado = propio.idEmpleado;
    }
  }

  /** Lista de responsables que trae la respuesta. */
  protected abstract listaResponsables(res: RespuestaIso<TIncidencia>): Empleado[];

  /** Incidencia con los campos del responsable elegido que se envía al pasarla a proceso. */
  protected abstract crearAsignacion(responsable: Empleado): TIncidencia;

  private deduplicarResponsables(responsables: Empleado[]): Empleado[] {
    const vistos = new Set<number>();
    return responsables.filter((responsable) => {
      if (!responsable?.idEmpleado || vistos.has(responsable.idEmpleado)) {
        return false;
      }
      vistos.add(responsable.idEmpleado);
      return true;
    });
  }
}
